import { useMemo, useState } from 'react'
import { useServiceHistory } from '@/api/queries'
import type { ScaleHistoryEntry, ServiceInfo } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge, type Tone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { ago, dateTime } from '@/lib/format'
import styles from './MonitoringPage.module.css'
import { targetLabel, type Target } from './target'

type Row = ScaleHistoryEntry & { id: number }

const WINDOWS = [
  { label: '1 h', hours: 1 },
  { label: '6 h', hours: 6 },
  { label: '12 h', hours: 12 },
  { label: '24 h', hours: 24 },
  { label: '7 d', hours: 168 },
]

const STATUS_TONE: Record<string, Tone> = { Succeeded: 'ok', Failed: 'danger' }

const text = (value: string | null) => (
  <span className={styles.historyText} title={value ?? undefined}>
    {value ?? '–'}
  </span>
)

const columns: Column<Row>[] = [
  { key: 'date', header: 'Date', width: '170px', render: (h) => <span title={ago(h.date)}>{dateTime(h.date, 'yyyy-MM-dd HH:mm:ss')}</span> },
  { key: 'operationName', header: 'Operation' },
  {
    key: 'status',
    header: 'Status',
    width: '110px',
    render: (h) => (
      <Badge tone={STATUS_TONE[h.status] ?? 'neutral'} title={h.status === 'None' ? 'The operation only logged information' : undefined}>
        {h.status}
      </Badge>
    ),
  },
  { key: 'description', header: 'Description', hideBelow: 'sm', render: (h) => text(h.description) },
  { key: 'externalDescription', header: 'External description', hideBelow: 'md', render: (h) => text(h.externalDescription) },
]

interface ServiceHistoryDialogProps {
  target: Target
  service: ServiceInfo
  onClose: () => void
}

export function ServiceHistoryDialog({ target, service, onClose }: ServiceHistoryDialogProps) {
  const [hours, setHours] = useState(24)
  const { data, isLoading, error } = useServiceHistory(target, service.name, hours)
  const rows = useMemo(() => data?.map((entry, id) => ({ ...entry, id })), [data])

  return (
    <Modal
      title={`${service.name} · history · ${targetLabel(target)}`}
      onClose={onClose}
      size="lg"
      headerExtra={
        <div className="u-row u-wrap" role="group" aria-label="Time window">
          {WINDOWS.map((option) => (
            <Button key={option.hours} size="sm" active={hours === option.hours} onClick={() => setHours(option.hours)}>
              {option.label}
            </Button>
          ))}
        </div>
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(h) => h.id}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search an operation or description"
        dense
        initialSort={{ key: 'date', direction: 'desc' }}
        emptyMessage="No operation in this time window"
        expandable={(h) => (
          <div className={styles.historyDetails}>
            <strong>Description</strong>
            <pre>{h.description ?? 'None'}</pre>
            <strong>External description</strong>
            <pre>{h.externalDescription ?? 'None'}</pre>
          </div>
        )}
      />
    </Modal>
  )
}
